import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, Divider, FormControl, InputLabel, MenuItem, Select, Typography, Checkbox, FormControlLabel, ListItemText } from "@material-ui/core";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import { editAgreementPermissions, getPermissions } from "../../../../actions";
import ActionDialog from "../../ActionDialog";
import { useManagementAction } from "../../../../hooks/useManagementAction";

const ChangePermissions = ({ open, handleCloseDialog, user, fetchUser }) => {
  const { handleManagementAction } = useManagementAction();

  const initialPermissions = user?.contractAgent?.permissions.map((p) => p.id) || [];
  const defaultPermissions = user?.contractAgent?.contractAgentClass?.permissions.map((p) => p.id) || [];
  const [selectedPermissions, setSelectedPermissions] = useState(new Set(initialPermissions));
  const [loading, setLoading] = useState(false);
  const [useDefault, setUseDefault] = useState(user.contractAgent.defaultPermissions);
  const [permissionsList, setPermissionsList] = useState([]);

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const permissionsData = await getPermissions();
      setPermissionsList(permissionsData);
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleSelectChange = (event) => {
    setSelectedPermissions(new Set(event.target.value));
    if (useDefault) setUseDefault(false);
  };

  const handleUseDefaultChange = () => {
    const permissions = useDefault ? initialPermissions : defaultPermissions;
    setSelectedPermissions(new Set(permissions));
    setUseDefault((prev) => !prev);
  };

  const onFinish = () => {
    if (!isMountedRef.current) return;

    setLoading(false);
    handleCloseDialog();
  };

  const handleSubmit = async () => {
    setLoading(true);
    const permissionIds = useDefault ? [] : Array.from(selectedPermissions);
    await handleManagementAction({
      body: { editedUserId: user.id, permissionIds },
      managementAction: editAgreementPermissions,
      onComplete: fetchUser,
      onFinish,
      successMessage: "app.edit.sucesfully",
    });
  };

  return (
    <ActionDialog
      title="app.edit"
      open={open}
      onAccept={handleSubmit}
      onClose={() => handleCloseDialog()}
      textConfirm="app.edit"
      maxWidth="xs"
      disableConfirmButton={loading}
      textToConfirmation={"app.confirm.changePermissions"}
    >
      <FormControl
        variant="outlined"
        fullWidth
        disabled={loading || useDefault}
        size="small"
      >
        <InputLabel
          id="permissions-label"
          shrink
        >
          <FormattedMessage id="app.manager.permissions" />
        </InputLabel>
        <Select
          labelId="permissions-label"
          multiple
          MenuProps={{
            getContentAnchorEl: null,
            anchorOrigin: {
              vertical: "center",
              horizontal: "center",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "center",
            },
          }}
          value={Array.from(selectedPermissions)}
          onChange={handleSelectChange}
          label={<FormattedMessage id="app.manager.permissions" />}
          displayEmpty
          notched
          renderValue={(selected) => {
            const value = selected.length;
            const message = value === 0 ? "app.contractAgent.permissions.none" : value === 1 ? "app.contractAgent.permissions.one" : "app.contractAgent.permissions";

            return (
              <Typography component="span">
                <FormattedHTMLMessage
                  id={message}
                  values={{ value }}
                />
              </Typography>
            );
          }}
        >
          {permissionsList.map(({ id, name }) => (
            <MenuItem
              key={id}
              value={id}
              disabled={loading || useDefault}
            >
              <Checkbox
                checked={selectedPermissions.has(id)}
                color="primary"
              />
              <ListItemText
                primary={
                  <FormattedHTMLMessage
                    id={`app.enuns.permissions.${name}`}
                    defaultMessage={name}
                  />
                }
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <br />
      <Divider variant="middle" />
      <br />
      <FormControlLabel
        disabled={loading}
        control={
          <Checkbox
            color="primary"
            checked={useDefault}
            onChange={handleUseDefaultChange}
            name="useDefault"
          />
        }
        label={
          <Typography>
            <FormattedMessage id="app.manager.useDefault" />
          </Typography>
        }
      />
      {loading && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <CircularProgress />
        </div>
      )}
    </ActionDialog>
  );
};

export default ChangePermissions;
